import React, { useState, useEffect } from 'react';

const RiskAssessmentTool = () => {
  const [selectedSector, setSelectedSector] = useState('energy');
  const [organizationSize, setOrganizationSize] = useState('medium');
  const [impactLevels, setImpactLevels] = useState({
    sabotage: 'high',
    informationtheft: 'high',
    crime: 'high',
    hactivism: 'medium',
    disinformation: 'low'
  });
  const [threatProbabilities, setThreatProbabilities] = useState({
    sabotage: {
      competitors: 'low',
      ideologues: 'low',
      terrorist: 'medium',
      cybercriminals: 'medium',
      nationstate: 'high'
    },
    informationtheft: {
      competitors: 'low',
      ideologues: 'low',
      terrorist: 'low',
      cybercriminals: 'high',
      nationstate: 'high'
    },
    crime: {
      competitors: 'low',
      ideologues: 'low',
      terrorist: 'low',
      cybercriminals: 'high',
      nationstate: 'low'
    },
    hactivism: {
      competitors: 'low',
      ideologues: 'medium',
      terrorist: 'low',
      cybercriminals: 'low',
      nationstate: 'medium'
    },
    disinformation: {
      competitors: 'low',
      ideologues: 'medium',
      terrorist: 'low',
      cybercriminals: 'low',
      nationstate: 'low'
    }
  });
  const [riskScores, setRiskScores] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const [cyfunLevel, setCyfunLevel] = useState('BASIC');

  const sectors = {
    energy: 'Energy',
    transport: 'Transport',
    finance: 'Finance',
    healthcare: 'Healthcare',
    water: 'Water',
    ict: 'ICT',
    food: 'Food',
    manufacturing: 'Manufacturing',
    chemicals: 'Chemicals',
    aerospace: 'Aerospace',
    research: 'Research',
    postal: 'Postal',
    waste: 'Waste',
    space: 'Space',
    publicadmin: 'Public Administration',
    socialmedia: 'Social Media'
  };

  const attackCategories = {
    sabotage: 'Sabotage',
    informationtheft: 'Information Theft',
    crime: 'Crime',
    hactivism: 'Hactivism',
    disinformation: 'Disinformation'
  };

  const threatActors = {
    competitors: 'Competitors',
    ideologues: 'Ideologues',
    terrorist: 'Terrorist',
    cybercriminals: 'Cyber Criminals',
    nationstate: 'Nation State'
  };

  const sectorRiskData = {
    energy: {
      impactLevels: {
        sabotage: 'high',
        informationtheft: 'high',
        crime: 'high',
        hactivism: 'medium',
        disinformation: 'low'
      },
      attackTypes: {
        sabotage: 'targeted',
        informationtheft: 'targeted',
        crime: 'global',
        hactivism: 'global',
        disinformation: 'global'
      }
    },
    transport: {
      impactLevels: {
        sabotage: 'high',
        informationtheft: 'high',
        crime: 'high',
        hactivism: 'medium',
        disinformation: 'low'
      },
      attackTypes: {
        sabotage: 'targeted',
        informationtheft: 'targeted',
        crime: 'global',
        hactivism: 'global',
        disinformation: 'global'
      }
    },
    finance: {
      impactLevels: {
        sabotage: 'high',
        informationtheft: 'high',
        crime: 'high',
        hactivism: 'medium',
        disinformation: 'low'
      },
      attackTypes: {
        sabotage: 'targeted',
        informationtheft: 'targeted',
        crime: 'global',
        hactivism: 'global',
        disinformation: 'global'
      }
    },
    healthcare: {
      impactLevels: {
        sabotage: 'high',
        informationtheft: 'high',
        crime: 'high',
        hactivism: 'medium',
        disinformation: 'low'
      },
      attackTypes: {
        sabotage: 'targeted',
        informationtheft: 'targeted',
        crime: 'global',
        hactivism: 'global',
        disinformation: 'global'
      }
    }
  };

  const probabilityLookup = {
    low: 0,
    medium: 0.5,
    high: 1
  };

  const impactLookup = {
    low: 0,
    medium: 5,
    high: 10
  };

  const organizationSizeLookup = {
    small: 1,
    medium: 2,
    large: 3
  };

  const attackTypeLookup = {
    global: 1,
    targeted: 2
  };

  const handleThreatActorChange = (category, actor, probability) => {
    setThreatProbabilities(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [actor]: probability
      }
    }));
  };

  const handleImpactChange = (category, impact) => {
    setImpactLevels(prev => ({
      ...prev,
      [category]: impact
    }));
  };

  const calculateRiskScores = () => {
    const scores = {};
    let total = 0;

    Object.keys(attackCategories).forEach(category => {
      const categoryScores = {};
      
      Object.keys(threatActors).forEach(actor => {
        const probValue = threatProbabilities[category]?.[actor];
        const probability = probabilityLookup[probValue] || 0;
        const impact = impactLookup[impactLevels[category]] || 0;
        const attackTypeLevel = sectorRiskData[selectedSector]?.attackTypes?.[category] || 'targeted';
        const attackType = attackTypeLookup[attackTypeLevel] || 2;
        const orgSize = organizationSizeLookup[organizationSize] || 2;
        
        const score = probability * impact * attackType * orgSize;
        categoryScores[actor] = score;
        total += score;
      });
      
      scores[category] = categoryScores;
    });

    setRiskScores(scores);
    setTotalScore(total);
  };

  const determineCyfunLevel = () => {
    if (totalScore >= 200) {
      setCyfunLevel('ESSENTIAL');
    } else if (totalScore >= 100) {
      setCyfunLevel('IMPORTANT');
    } else {
      setCyfunLevel('BASIC');
    }
  };

  useEffect(() => {
    calculateRiskScores();
  }, [selectedSector, organizationSize, threatProbabilities, impactLevels]);

  useEffect(() => {
    determineCyfunLevel();
  }, [totalScore]);

  const getRiskLevelColor = (score) => {
    if (score >= 20) return 'bg-red-500';
    if (score >= 10) return 'bg-orange-500';
    if (score >= 5) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getCyfunLevelColor = () => {
    switch (cyfunLevel) {
      case 'ESSENTIAL': return 'text-red-600 bg-red-100';
      case 'IMPORTANT': return 'text-orange-600 bg-orange-100';
      case 'BASIC': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Configuration</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sector
                </label>
                <select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {Object.entries(sectors).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Size
                </label>
                <select
                  value={organizationSize}
                  onChange={(e) => setOrganizationSize(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Impact Levels by Attack Category</h2>
            <p className="text-sm text-gray-600 mb-4">Select the impact level for each cyber attack category</p>
            
            <div className="space-y-4">
              {Object.entries(attackCategories).map(([categoryKey, categoryLabel]) => (
                <div key={categoryKey} className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700 w-48">
                    {categoryLabel}
                  </label>
                  <select
                    value={impactLevels[categoryKey]}
                    onChange={(e) => handleImpactChange(categoryKey, e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low (0)</option>
                    <option value="medium">Medium (5)</option>
                    <option value="high">High (10)</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Threat Actor Probabilities by Attack Category</h2>
            
            <div className="space-y-6">
              {Object.entries(attackCategories).map(([categoryKey, categoryLabel]) => (
                <div key={categoryKey} className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">{categoryLabel}</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {Object.entries(threatActors).map(([actorKey, actorLabel]) => (
                      <div key={actorKey} className="flex items-center space-x-3">
                        <label className="text-sm font-medium text-gray-700 w-32">
                          {actorLabel}
                        </label>
                        <select
                          value={threatProbabilities[categoryKey][actorKey]}
                          onChange={(e) => handleThreatActorChange(categoryKey, actorKey, e.target.value)}
                          className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Risk Assessment Results</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">Total Risk Score</p>
                <p className="text-3xl font-bold text-gray-900">{totalScore.toFixed(1)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">CyFun Assurance Level</p>
                <p className={`text-2xl font-bold px-4 py-2 rounded-lg ${getCyfunLevelColor()}`}>
                  {cyfunLevel}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">Attack Category</th>
                    {Object.entries(threatActors).map(([key, label]) => (
                      <th key={key} className="border border-gray-300 px-4 py-2 text-center">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(attackCategories).map(([categoryKey, categoryLabel]) => (
                    <tr key={categoryKey}>
                      <td className="border border-gray-300 px-4 py-2 font-medium">
                        {categoryLabel}
                      </td>
                      {Object.keys(threatActors).map(actorKey => (
                        <td key={actorKey} className="border border-gray-300 px-4 py-2 text-center">
                          <div className={`px-2 py-1 rounded text-white text-sm ${getRiskLevelColor(riskScores[categoryKey]?.[actorKey] || 0)}`}>
                            {(riskScores[categoryKey]?.[actorKey] || 0).toFixed(1)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">CyFun Level Guidelines</h2>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <div>
                  <p className="font-medium">BASIC (0-99)</p>
                  <p className="text-sm text-gray-600">Fundamental cybersecurity measures</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <div>
                  <p className="font-medium">IMPORTANT (100-199)</p>
                  <p className="text-sm text-gray-600">Enhanced cybersecurity measures</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <div>
                  <p className="font-medium">ESSENTIAL (200+)</p>
                  <p className="text-sm text-gray-600">Comprehensive cybersecurity measures</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAssessmentTool;